import {
  FormattedPrice,
  isImageOr3dModel,
  Modal,
  processAttachment,
  RichTextValue,
} from "@msys/ui";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Button, IconButton, Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { ApplyItemActionSetRecommendationsOperation } from "../../../../clients/graphqlTypes.js";
import { GallerySlider } from "../../../commons/images/GallerySlider.js";
import { PropertyOptionItem } from "../../doc-items/PropertyOptionItem.js";
import {
  WizardQuestions_AttachmentFragment,
  WizardQuestions_Question_WizardMultipleRecommendationsQuestion_Fragment,
} from "../../documents/WizardQuestions.generated.js";
import { recommendationsAction } from "./answerToItemAction.js";
import { getQuestionPrompt } from "./questionShell.js";
import {
  WizardRendererProps,
  WizardRendererRef,
} from "./WizardRendererProps.js";
import { WizardTileGrid } from "./WizardTileGrid.js";

type Recommendation =
  WizardQuestions_Question_WizardMultipleRecommendationsQuestion_Fragment["recommendations"][number];

const MEDIA_HEIGHT = 240;
// A fixed title id: the dialog would otherwise take a React-generated `:r…:`
// id, which jsdom's selector engine cannot match against the tiles' `:has()`
// hover rule.
const DETAILS_TITLE_ID = "wizard-recommendation-details-title";

const describe = (recommendation: Recommendation) =>
  recommendation.__typename === "WizardRecommendationTemplate"
    ? {
        id: recommendation.templateId,
        title: recommendation.templateTitle,
        description: recommendation.templateDescription,
        price: recommendation.templatePrice,
        currency: recommendation.templateCurrency,
        media: recommendation.templateMedia,
      }
    : {
        id: recommendation.templateTypeId,
        title: recommendation.templateTypeTitle,
        description: recommendation.templateTypeDescription,
        price: recommendation.templateTypePrice,
        currency: recommendation.templateTypeCurrency,
        media: recommendation.templateTypeMedia,
      };

/**
 * Recommended additions as a multi-select tile gallery, the way the
 * end-customer wizard offers them. A tile carries the title and picture;
 * price and description live in the details dialog, which also toggles the
 * recommendation and steps to its neighbours. The answer stays staged and is
 * submitted through the ref by the action bar's forward button.
 */
export const WizardRecommendationsRenderer = React.forwardRef<
  WizardRendererRef,
  WizardRendererProps<WizardQuestions_Question_WizardMultipleRecommendationsQuestion_Fragment>
>(({ question, onAnswer, disabled }, ref) => {
  const { t } = useTranslate(["Global", "Quote"]);

  const described = React.useMemo(
    () => question.recommendations.map(describe),
    [question.recommendations]
  );

  const initial = React.useMemo(
    () =>
      Object.fromEntries(
        question.recommendations.map(r => [describe(r).id, r.included])
      ) as Record<string, boolean>,
    [question.recommendations]
  );
  const [included, setIncluded] = React.useState(initial);
  const [openIndex, setOpenIndex] = React.useState<number | null>(null);

  React.useEffect(() => setIncluded(initial), [initial]);

  const submit = React.useCallback(
    () =>
      onAnswer(
        recommendationsAction(
          question.itemId,
          Object.entries(included).map(([recommendationId, isIncluded]) => ({
            recommendationId,
            operation: (isIncluded
              ? "include"
              : "exclude") as ApplyItemActionSetRecommendationsOperation,
          }))
        )
      ),
    [included, onAnswer, question.itemId]
  );

  React.useImperativeHandle(ref, () => ({ submit: () => void submit() }), [
    submit,
  ]);

  const toggle = (id: string) => {
    setIncluded(current => ({ ...current, [id]: !current[id] }));
  };

  const imageOf = (media: ReadonlyArray<WizardQuestions_AttachmentFragment>) =>
    media.find(m => m.mimeType.startsWith("image/")) ?? null;
  const withImage = described.some(r => imageOf(r.media));

  const open = openIndex !== null ? (described[openIndex] ?? null) : null;

  return (
    <>
      <WizardTileGrid label={getQuestionPrompt(question)}>
        {described.map((recommendation, index) => (
          <PropertyOptionItem
            key={recommendation.id}
            multiple
            label={recommendation.title}
            isPreselected={included[recommendation.id] ?? false}
            isGreyedOut={!included[recommendation.id]}
            image={imageOf(recommendation.media)}
            withImage={withImage}
            disabled={disabled}
            onClick={() => toggle(recommendation.id)}
            onDetailsClick={() => setOpenIndex(index)}
            detailsLabel={t("Details", { ns: "Quote" })}
          />
        ))}
      </WizardTileGrid>
      {open && openIndex !== null && (
        <RecommendationDetails
          recommendation={open}
          included={included[open.id] ?? false}
          disabled={disabled}
          onToggle={() => toggle(open.id)}
          onPrevious={
            openIndex > 0 ? () => setOpenIndex(openIndex - 1) : undefined
          }
          onNext={
            openIndex < described.length - 1
              ? () => setOpenIndex(openIndex + 1)
              : undefined
          }
          onClose={() => setOpenIndex(null)}
        />
      )}
    </>
  );
});

const RecommendationDetails = ({
  recommendation,
  included,
  disabled,
  onToggle,
  onPrevious,
  onNext,
  onClose,
}: {
  recommendation: ReturnType<typeof describe>;
  included: boolean;
  disabled: boolean;
  onToggle: () => void;
  onPrevious: (() => void) | undefined;
  onNext: (() => void) | undefined;
  onClose: () => void;
}) => {
  const { t } = useTranslate(["Global", "Quote"]);
  const { title, description, price, currency, media } = recommendation;

  const images = media.map(processAttachment).filter(isImageOr3dModel);

  return (
    <Modal
      title={title}
      maxWidth="sm"
      dialogProps={{ "aria-labelledby": DETAILS_TITLE_ID }}
      handleClose={onClose}
      dialogActions={
        <Stack
          direction="row"
          spacing={1}
          sx={{ width: "100%", justifyContent: "space-between" }}
        >
          <IconButton
            aria-label={t("Previous", { ns: "Global" })}
            disabled={!onPrevious}
            onClick={onPrevious}
          >
            <ChevronLeftIcon />
          </IconButton>
          <Button
            variant={included ? "outlined" : "contained"}
            disabled={disabled}
            onClick={onToggle}
          >
            {included
              ? t("Deselect", { ns: "Quote" })
              : t("Select", { ns: "Global" })}
          </Button>
          <IconButton
            aria-label={t("Next", { ns: "Global" })}
            disabled={!onNext}
            onClick={onNext}
          >
            <ChevronRightIcon />
          </IconButton>
        </Stack>
      }
    >
      <Stack direction="column" spacing={2}>
        {images.length > 0 && (
          <GallerySlider images={images} height={MEDIA_HEIGHT} />
        )}
        {price !== null && price !== undefined && (
          <Typography variant="subtitle1">
            <FormattedPrice value={{ amount: price, currency }} />
          </Typography>
        )}
        {description && <RichTextValue htmlContent={description} />}
      </Stack>
    </Modal>
  );
};
